import { useState, useEffect } from 'react';
import './WidgetBanner.css';

/* ---- Greeting based on time of day ---- */
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 5) return 'Good Night';
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  if (hour < 21) return 'Good Evening';
  return 'Good Night';
}

/* ---- Format date ---- */
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/* ---- Format time ---- */
function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/* ---- Weather condition icon mapping ---- */
function getWeatherIcon(condition: string): string {
  const hour = new Date().getHours();
  const isNight = hour < 6 || hour >= 19;

  switch (condition.toLowerCase()) {
    case 'clear':
      return isNight ? '🌙' : '☀️';
    case 'clouds':
    case 'cloudy':
      return isNight ? '☁️' : '⛅';
    case 'rain':
    case 'drizzle':
      return '🌧️';
    case 'thunderstorm':
      return '⛈️';
    case 'snow':
      return '🌨️';
    case 'mist':
    case 'fog':
    case 'haze':
      return '🌫️';
    default:
      return isNight ? '🌙' : '🌤️';
  }
}

interface WeatherData {
  temp: number;
  condition: string;
  location: string;
}

export function WidgetBanner() {
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [notifications] = useState<{ id: string; text: string; icon: string }[]>([]);

  // Update clock every 30 seconds
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  // Fetch weather data
  useEffect(() => {
    let cancelled = false;

    async function fetchWeather() {
      try {
        const resp = await fetch('/api/weather?units=metric');
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled) return;

        if (data?.success && data.data) {
          setWeather({
            temp: Math.round(data.data.temp ?? data.data.temperature ?? 0),
            condition: data.data.condition ?? data.data.weather ?? 'Clear',
            location: data.data.city ?? data.data.location ?? '',
          });
        }
      } catch {
        // silently fail - widget will show without weather
      }
    }

    fetchWeather();
    return () => { cancelled = true; };
  }, []);

  const greeting = getGreeting();
  const dateStr = formatDate(now);
  const timeStr = formatTime(now);

  return (
    <div className="widget-banner">
      <div className="widget-banner__scroll">
        {/* Date & Time Widget */}
        <div className="widget-card widget-card--datetime">
          <div className="widget-card__row">
            <span className="widget-card__time">{timeStr}</span>
          </div>
          <div className="widget-card__greeting">{greeting}</div>
          <div className="widget-card__date">{dateStr}</div>
        </div>

        {/* Weather Widget */}
        <a href="/weather" className="widget-card widget-card--weather">
          <div className="widget-card__weather-icon">
            {getWeatherIcon(weather?.condition ?? '')}
          </div>
          {weather ? (
            <>
              <div className="widget-card__temp">{weather.temp}°</div>
              <div className="widget-card__location">{weather.location || 'Weather'}</div>
            </>
          ) : (
            <>
              <div className="widget-card__temp">--°</div>
              <div className="widget-card__location">Weather</div>
            </>
          )}
        </a>

        {/* Notifications / Quick Actions Widget */}
        <a href="/chat" className="widget-card widget-card--notif">
          <div className="widget-card__notif-header">
            <span className="widget-card__notif-icon">💬</span>
            <span className="widget-card__notif-title">Messages</span>
          </div>
          {notifications.length > 0 ? (
            <div className="widget-card__notif-list">
              {notifications.slice(0, 2).map((n) => (
                <div key={n.id} className="widget-card__notif-item">
                  <span>{n.icon}</span>
                  <span>{n.text}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="widget-card__notif-empty">
              No new messages
            </div>
          )}
        </a>

        {/* Quick Info Banner Widget */}
        <div className="widget-card widget-card--banner">
          <div className="widget-card__banner-icon">🎮</div>
          <div className="widget-card__banner-text">
            <div className="widget-card__banner-title">Games</div>
            <div className="widget-card__banner-sub">Play & earn credits</div>
          </div>
        </div>
      </div>
    </div>
  );
}
